import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import { LANGUAGES, CEFR_LEVELS } from '@/lib/languages'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { CheckCircle2, Target, Sparkles, ArrowRight, Loader2 } from 'lucide-react'

interface RoadmapData {
  id: string
  unit_ids: string[]
  current_unit_index: number
}

export function RoadmapReady() {
  const navigate = useNavigate()
  const { profile } = useAuth()
  const [roadmap, setRoadmap] = useState<RoadmapData | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const targetLanguage = LANGUAGES.find(l => l.code === profile?.target_language)
  const assessedLevel = CEFR_LEVELS.find(l => l.code === profile?.level)

  useEffect(() => {
    const fetchRoadmap = async () => {
      if (!profile?.id) return

      const { data } = await supabase
        .from('user_roadmap')
        .select('id, unit_ids, current_unit_index')
        .eq('user_id', profile.id)
        .eq('status', 'active')
        .single()

      setRoadmap(data)
      setIsLoading(false)
    }

    fetchRoadmap()
  }, [profile?.id])

  // If user hasn't completed onboarding, redirect to home
  useEffect(() => {
    if (profile && !profile.onboarding_completed) {
      navigate('/')
    }
  }, [profile, navigate])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">Loading your learning plan...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto space-y-8">
          {/* Success Header */}
          <div className="text-center space-y-4">
            <div className="mx-auto h-20 w-20 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold">Your Learning Plan is Ready!</h1>
            <p className="text-lg text-muted-foreground">
              Based on our conversation, I've created a personalized plan just for you.
            </p>
          </div>

          {/* Level Assessment Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5 text-primary" />
                Your Assessed Level
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-4xl font-bold text-primary">{assessedLevel?.code}</p>
                  <p className="text-muted-foreground">{assessedLevel?.name}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Learning</p>
                  <p className="font-medium">{targetLanguage?.name}</p>
                </div>
              </div>
              {profile?.level_assessed_at && (
                <p className="text-xs text-muted-foreground mt-4">
                  Assessed on {new Date(profile.level_assessed_at).toLocaleDateString()}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Goals Card */}
          {profile?.goals && profile.goals.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  Your Learning Goals
                </CardTitle>
                <CardDescription>
                  What you want to achieve
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {profile.goals.map((goal, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                      <span>{goal}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Interests Card */}
          {profile?.interests && profile.interests.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Your Interests</CardTitle>
                <CardDescription>
                  Topics we'll use in your lessons
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {profile.interests.map((interest, i) => (
                    <span
                      key={i}
                      className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm"
                    >
                      {interest}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Roadmap Preview */}
          {roadmap && roadmap.unit_ids.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Your Learning Path</CardTitle>
                <CardDescription>
                  {roadmap.unit_ids.length} lessons personalized for you
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
                    <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
                      1
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">First Lesson</p>
                      <p className="text-sm text-muted-foreground">Ready to start</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-primary" />
                  </div>
                  <div className="text-center text-sm text-muted-foreground py-2">
                    + {roadmap.unit_ids.length - 1} more lessons
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Separator />

          {/* CTA */}
          <div className="text-center space-y-4">
            <Button
              size="lg"
              className="w-full max-w-xs h-14 text-lg"
              onClick={() => navigate('/')}
            >
              Start Learning
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <p className="text-sm text-muted-foreground">
              Your personalized lessons are waiting for you
            </p>
          </div>
        </div>
      </main>
    </div>
  )
}
